import React, { useState, useEffect } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import { normalizeDocumentSearchProperties } from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import DocumentSearchDataGrid from "../../components/DataTables/DocumentSearchDataGrid.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";

const DOCUMENT_TYPES = {
  0: "Полици",
  1: "Сметки",
  2: "Стикери",
  3: "Други ЦК",
  4: "СЗК",
};

const DOCUMENT_STATUS = {
  0: "Празни",
  1: "Активни",
  2: "Приключени",
  3: "Прекратени",
  4: "Анулирани",
};

const labelClass =
  "mb-1 ml-1 block text-xs font-medium text-black dark:text-white";

const EmptyDocuments: React.FC = () => {
  const [formState, setFormState] = useState({
    documentType: "0",
    insuranceCompany: "all",
    insuranceType: "all",
    agent: "all",
    office: "all",
    documentStatus: "0",
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [policies, setPolicies] = useState<any[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [offices, setOffices] = useState<{ [key: string]: string }>({});
  const [agents, setAgents] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setFormState({
      documentType: "0",
      documentStatus: "0",
      insuranceType: "all",
      insuranceCompany: "all",
      office: "all",
      agent: "all",
    });
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [companies, policyTypesRes, officesRes, agentsRes] =
          await Promise.all([
            InsuranceService.getAllInsuranceCompanies(jwt),
            InsuranceService.getAllPolicyTypes(jwt),
            InsuranceService.getAllOffices(jwt),
            InsuranceService.getAllAgents(jwt),
          ]);
        setInsuranceCompanies({
          all: "Всички",
          ...(companies as { [key: string]: string }),
        });
        setPolicyTypes({
          all: "Всички",
          ...(policyTypesRes as { [key: string]: string }),
        });
        setOffices({
          all: "Всички",
          ...(officesRes as { [key: string]: string }),
        });
        setAgents({
          all: "Всички",
          ...(agentsRes as { [key: string]: string }),
        });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const result = await PolicyService.fetchEmptyDocuments(formState, jwt);
      const normalizedPolicies = Object.values(result).map((policy) => {
        if (!policy.InsComp)
          policy.InsComp = insuranceCompanies[formState.insuranceCompany];
        if (!policy.Status)
          policy.Status = DOCUMENT_STATUS[formState.documentStatus];
        return normalizeDocumentSearchProperties(policy);
      });
      setPolicies(normalizedPolicies);
    } catch (error) {
      setAlert("Грешка при зареждането на полици.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
    setAlert(null);
  };

  return (
    <>
      <Breadcrumb pageName="Празни документи" />
      <FadeAlert
        alert={alert}
        type={alertType}
        onClose={() => setAlert(null)}
      />

      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
        <SectionHeader title="Празни документи" />

        <form onSubmit={handleSearch} className="p-3 pt-1 ">
          <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-6">
            <div>
              <label className={labelClass}>Тип документ</label>
              <SelectGroup
                value={formState.documentType}
                placeholder="Изберете тип документ"
                options={DOCUMENT_TYPES}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, documentType: value }))
                }
                name="documentType"
              />
            </div>
            <div>
              <label className={labelClass}>Статус</label>
              <SelectGroup
                value={formState.documentStatus}
                placeholder="Изберете статус"
                options={DOCUMENT_STATUS}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, documentStatus: value }))
                }
                name="documentStatus"
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.insuranceCompany}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, insuranceCompany: value }))
                }
                name="insuranceCompany"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.insuranceType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, insuranceType: value }))
                }
                name="insuranceType"
              />
            </div>
            <div>
              <label className={labelClass}>Офис</label>
              <SelectGroup
                value={formState.office}
                placeholder="Офис"
                options={offices}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, office: value }))
                }
                name="office"
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Агент"
                options={agents}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
          </div>

          <div className="mt-3 flex justify-center">
            <button
              type="submit"
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Покажи
            </button>
          </div>
        </form>
      </fieldset>

      <DocumentSearchDataGrid policies={policies} isLoading={isLoading} />
    </>
  );
};

export default EmptyDocuments;
